import type { ReactNode } from 'react'

/** Tamanho de página das tabelas de lista (Atendimento HUB, Ocorrências) —
 * o mesmo da fila de Solicitações (`SolicitationTable.ITEMS_PER_PAGE`). */
export const TABLE_PAGE_SIZE = 12

/** Janela de até 5 números de página, deslizando ao redor da atual. */
function getPageNumbers(current: number, total: number): number[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
  let start = Math.max(1, current - 2)
  const end = Math.min(total, start + 4)
  start = Math.max(1, end - 4)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

interface TablePaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Plural do que está sendo listado — "problemas", "processos"… */
  itemLabel: string
  /** Texto da esquerda no lugar de "Mostrando 1–12 de N {itemLabel}". */
  summary?: ReactNode
}

const PAGE_BTN =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2.5 text-[13px] font-medium transition disabled:cursor-not-allowed'

/** Rodapé das tabelas de lista: resumo à esquerda; Anterior/números/Próximo
 * à direita (só com mais de uma página). */
export function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  itemLabel,
  summary,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = (page - 1) * pageSize

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-5 py-3.5">
      <div className="text-[13px] text-muted">
        {summary ??
          `Mostrando ${total === 0 ? 0 : start + 1}–${Math.min(start + pageSize, total)} de ${total.toLocaleString('pt-BR')} ${itemLabel}`}
      </div>
      {totalPages > 1 && (
        <nav className="flex items-center gap-1.5" aria-label="Paginação">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page === 1}
            className={`${PAGE_BTN} border-border-subtle bg-surface px-3 text-body hover:bg-surface-muted disabled:text-subtle disabled:hover:bg-surface`}
          >
            Anterior
          </button>
          {getPageNumbers(page, totalPages).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onPageChange(n)}
              aria-current={page === n ? 'page' : undefined}
              className={`${PAGE_BTN} ${
                page === n
                  ? 'border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                  : 'border-border-subtle bg-surface text-body hover:bg-surface-muted'
              }`}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onPageChange(Math.min(page + 1, totalPages))}
            disabled={page === totalPages}
            className={`${PAGE_BTN} border-border-subtle bg-surface px-3 text-body hover:bg-surface-muted disabled:text-subtle disabled:hover:bg-surface`}
          >
            Próximo
          </button>
        </nav>
      )}
    </div>
  )
}
